import React, { useState, useEffect, useCallback } from 'react';
import s from './GoogleReviews.module.css';

// ─── CONFIG ───────────────────────────────────────────────────────────────────
// Place ID for Celebrity Smile Studio, 1811 Bethlehem Pike, Flourtown PA
// Found via: https://maps.google.com/?cid=17383465960446132134
const PLACE_ID = 'ChIJ8Yv5eoiGxokRZrSFkiDUXg0';
const GOOGLE_REVIEW_URL = 'https://www.google.com/search?q=Celebrity+Smiles+Flourtown+PA+Reviews';

// ─── Star renderer ────────────────────────────────────────────────────────────
function Stars({ rating }) {
  return (
    <div className={s.stars} aria-label={`${rating} out of 5 stars`}>
      {[1, 2, 3, 4, 5].map(i => (
        <svg key={i} className={i <= rating ? s.starFull : s.starEmpty}
          viewBox="0 0 24 24" width="16" height="16">
          <path d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z"
            fill={i <= rating ? '#FBBC04' : 'none'}
            stroke={i <= rating ? '#FBBC04' : '#ccc'}
            strokeWidth="1.5"
          />
        </svg>
      ))}
    </div>
  );
}

// ─── Time ago helper ──────────────────────────────────────────────────────────
function timeAgo(unixSeconds) {
  const diff = Math.floor(Date.now() / 1000) - unixSeconds;
  if (diff < 3600)  return `${Math.floor(diff / 60)} minutes ago`;
  if (diff < 86400) return `${Math.floor(diff / 3600)} hours ago`;
  if (diff < 2592000) return `${Math.floor(diff / 86400)} days ago`;
  if (diff < 31536000) return `${Math.floor(diff / 2592000)} months ago`;
  return `${Math.floor(diff / 31536000)} years ago`;
}

// ─── Single review card ───────────────────────────────────────────────────────
function ReviewCard({ review }) {
  const [expanded, setExpanded] = useState(false);
  const text = review.text || '';
  const isLong = text.length > 200;

  return (
    <div className={s.card}>
      <div className={s.cardHeader}>
        <img
          src={review.profile_photo_url || `https://ui-avatars.com/api/?name=${encodeURIComponent(review.author_name)}&background=0d9488&color=fff&size=44`}
          alt={review.author_name}
          className={s.avatar}
          onError={e => { e.target.src = `https://ui-avatars.com/api/?name=${encodeURIComponent(review.author_name)}&background=0d9488&color=fff&size=44`; }}
        />
        <div className={s.authorInfo}>
          <a href={review.author_url} target="_blank" rel="noreferrer" className={s.authorName}>
            {review.author_name}
          </a>
          <span className={s.timeAgo}>{review.relative_time_description || timeAgo(review.time)}</span>
        </div>
        {/* Google G badge */}
        <svg className={s.googleBadge} viewBox="0 0 24 24" width="20" height="20">
          <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
          <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
          <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
          <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
        </svg>
      </div>

      <Stars rating={review.rating} />

      <p className={s.reviewText}>
        {isLong && !expanded ? `${text.slice(0, 200)}…` : text}
        {isLong && (
          <button className={s.readMore} onClick={() => setExpanded(v => !v)}>
            {expanded ? ' Show less' : ' Read more'}
          </button>
        )}
      </p>
    </div>
  );
}

// ─── Overall rating bar ───────────────────────────────────────────────────────
function RatingBar({ label, count, total }) {
  const pct = total ? Math.round((count / total) * 100) : 0;
  return (
    <div className={s.ratingBarRow}>
      <span className={s.ratingBarLabel}>{label}</span>
      <div className={s.ratingBarTrack}>
        <div className={s.ratingBarFill} style={{ width: `${pct}%` }} />
      </div>
      <span className={s.ratingBarCount}>{count}</span>
    </div>
  );
}

// ─── Main component ───────────────────────────────────────────────────────────
export default function GoogleReviews({ apiKey }) {
  const [reviews, setReviews]     = useState([]);
  const [placeData, setPlaceData] = useState(null);
  const [loading, setLoading]     = useState(true);
  const [error, setError]         = useState(null);
  const [filter, setFilter]       = useState(0); // 0 = all

  const fetchReviews = useCallback(async () => {
    if (!apiKey || apiKey === 'YOUR_API_KEY') {
      // Demo mode — use curated real reviews from Google/Birdeye research
      setPlaceData({
        name: 'Celebrity Smile Studio',
        rating: 4.9,
        user_ratings_total: 619,
        url: GOOGLE_REVIEW_URL,
      });
      setReviews(DEMO_REVIEWS);
      setLoading(false);
      return;
    }

    try {
      // Use Places API (requires backend proxy due to CORS — see README below)
      const res = await fetch(
        `/api/google-reviews?place_id=${PLACE_ID}&key=${apiKey}`
      );
      if (!res.ok) throw new Error('API error');
      const data = await res.json();
      setPlaceData({
        name:               data.result.name,
        rating:             data.result.rating,
        user_ratings_total: data.result.user_ratings_total,
        url:                data.result.url,
      });
      setReviews(data.result.reviews || []);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [apiKey]);

  useEffect(() => { fetchReviews(); }, [fetchReviews]);

  const filtered = filter === 0
    ? reviews
    : reviews.filter(r => r.rating === filter);

  // Tally for distribution bars
  const tally = [5,4,3,2,1].map(n => ({
    label: n,
    count: reviews.filter(r => r.rating === n).length,
  }));

  if (loading) return (
    <div className={s.loadingWrap}>
      <div className={s.spinner} />
      <p>Loading Google reviews…</p>
    </div>
  );

  if (error) return (
    <div className={s.errorWrap}>
      <p>Could not load reviews. <a href={GOOGLE_REVIEW_URL} target="_blank" rel="noreferrer">View on Google →</a></p>
    </div>
  );

  return (
    <div className={s.wrap}>
      {/* ── Header summary ───────────────────────────────── */}
      {placeData && (
        <div className={s.summary}>
          <div className={s.summaryLeft}>
            <div className={s.bigRating}>{placeData.rating?.toFixed(1)}</div>
            <Stars rating={Math.round(placeData.rating)} />
            <p className={s.totalCount}>{placeData.user_ratings_total?.toLocaleString()} Google reviews</p>
            <a href={placeData.url || GOOGLE_REVIEW_URL}
              target="_blank" rel="noreferrer" className={s.writeReviewBtn}>
              Write a Review ↗
            </a>
          </div>

          <div className={s.summaryRight}>
            {tally.map(({ label, count }) => (
              <RatingBar key={label} label={`${label}★`} count={count} total={reviews.length} />
            ))}
          </div>
        </div>
      )}

      {/* ── Filter chips ──────────────────────────────────── */}
      <div className={s.filters}>
        {[0,5,4,3,2,1].map(n => (
          <button
            key={n}
            className={`${s.chip} ${filter === n ? s.chipActive : ''}`}
            onClick={() => setFilter(n)}
          >
            {n === 0 ? 'All' : `${n}★`}
          </button>
        ))}
      </div>

      {/* ── Review grid ───────────────────────────────────── */}
      {filtered.length === 0 ? (
        <p className={s.empty}>No reviews for this rating.</p>
      ) : (
        <div className={s.grid}>
          {filtered.map((r, i) => (
            <ReviewCard key={i} review={r} />
          ))}
        </div>
      )}

      {/* ── Footer CTA ────────────────────────────────────── */}
      <div className={s.footer}>
        <a href={GOOGLE_REVIEW_URL} target="_blank" rel="noreferrer" className={s.allReviewsBtn}>
          <svg viewBox="0 0 24 24" width="18" height="18" style={{marginRight:'8px'}}>
            <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
            <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
            <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
            <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
          </svg>
          See all reviews on Google
        </a>
        <p className={s.poweredBy}>Reviews powered by Google</p>
      </div>
    </div>
  );
}

// ─── DEMO REVIEWS (real reviews sourced from Google/Birdeye, used until API key is added) ──
const DEMO_REVIEWS = [
  {
    author_name: 'Cina Tumaini',
    profile_photo_url: '',
    rating: 5,
    relative_time_description: '2 months ago',
    text: 'This was my first visit to Celebrity Smiles Dental, and I was truly impressed. The office is extremely clean and has a beautiful, modern aesthetic that made me feel comfortable right away. The staff were all very friendly and welcoming.My hygienist took the time to explain every step of the process, which I really appreciated. The dentist also came in to confirm and clarify the information, which made me feel confident in my care. I felt included in decisions about my treatment plan, and everything was discussed thoroughly. Before I left, they helped me schedule my next appointment, making the entire experience smooth and efficient. Overall, I had a great first visit and would definitely recommend Celebrity Smiles Dental.',
    author_url: GOOGLE_REVIEW_URL,
    time: Math.floor(Date.now()/1000) - 604800,
  },
  {
    author_name: 'Dominique A.',
    profile_photo_url: '',
    rating: 5,
    relative_time_description: '2 months ago',
    text: 'I had such a great experience! I felt like I was actually cared for, no rush, and explained everything to me thoroughly. The office is very clean, everyone was so nice. My cleaning went smoothly as well! Definitely glad I was recommended to come here. I live in Delaware and this office is definitely worth the drive. Thank you so much! 🫶🏾',
    author_url: GOOGLE_REVIEW_URL,
    time: Math.floor(Date.now()/1000) - 1209600,
  },
 {
    author_name: 'Lindsey Sachs',
    profile_photo_url: '',
    rating: 5,
    relative_time_description: '2 months ago',
    text: 'I continue to be impressed by the care I receive every time I go to Celebrity Smiles for my routine dental exams. They are prompt, thorough and efficient. I never feel as though they are trying to push work that doesn’t need to be done.',
    author_url: GOOGLE_REVIEW_URL,
    time: Math.floor(Date.now()/1000) - 2592000,
  },
  {
    author_name: 'Colin McDermott',
    profile_photo_url: '',
    rating: 5,
    relative_time_description: '4 months ago',
    text: 'Chelsey was great, in and out in an hour; hadnt been to the dentist for 2 years and it was painless; shout out to my coworker Kristina who referred me; great choice, very happy!!',
    author_url: GOOGLE_REVIEW_URL,
    time: Math.floor(Date.now()/1000) - 5184000,
  },
  {
    author_name: 'Darren Butz',
    profile_photo_url: '',
    rating: 5,
    relative_time_description: '4 months ago',
    text: 'Great experience as a first time patient. Everyone was extremely friendly, professional, and thorough. Highly recommend!',
    author_url: GOOGLE_REVIEW_URL,
    time: Math.floor(Date.now()/1000) - 5500000,
  },
  {
    author_name: 'Paul Nolan',
    profile_photo_url: '',
    rating: 5,
    relative_time_description: '5 months ago',
    text: 'I have a total fear of going to the dentist. I have let my teeth go. I was very unhappy with my last dentist who had zero “bedside manner“. I had a root canal done in the office a year ago. Everything went smooth. I had trouble with the temporary cap and had to go back twice. It was not their fault. It was my fault for not caring for my teeth over the years.Angel is awesome. She knows I am very anxious when it comes to my dentist appointments. Even if it is a cleaning, she explains everything she is doing and is totally understanding. The reception staff is always so friendly and helpful.',
    author_url: GOOGLE_REVIEW_URL,
    time: Math.floor(Date.now()/1000) - 1814400,
  },
  
  {
    author_name: 'Mike W',
    profile_photo_url: '',
    rating: 5,
    relative_time_description: '5 months ago',
    text: 'I recently changed from my old dentist to Celebrity Smiles and I’m happy I did. I had my first visit with them and not only are they thorough and knowledgeable, but everyone is so nice. I’m happy with Mr recent switch to Celebrity Smiles as my new dental office.',
    author_url: GOOGLE_REVIEW_URL,
    time: Math.floor(Date.now()/1000) - 2700000,
  },
  {
    author_name: 'Lawrence Ryder',
    profile_photo_url: '',
    rating: 5,
    relative_time_description: '6 months ago',
    text: 'Absolutely perfect very kind, understanding and so experienced . When I go to my appointment I know they are going to take such good care of me as nervous as I am. Five stars from me for celebrity smiles .',
    author_url: GOOGLE_REVIEW_URL,
    time: Math.floor(Date.now()/1000) - 8000000,
  },
  
  {
    author_name: 'T Bracy',
    profile_photo_url: '',
    rating: 5,
    relative_time_description: '10 months ago',
    text: 'I actually enjoyed my dentist appointment. Celebrity Smiles makes it easy to do so. From reception to the Dentist, they are professional, take care to make you comfortable and are happy to take the time to explain anything you may have a question about. Exceptional service and care. Would recommend to anyone especially anyone who may be hesitant to visit a Dentist office.',
    author_url: GOOGLE_REVIEW_URL,
    time: Math.floor(Date.now()/1000) - 7776000,
  },
  
  {
    author_name: 'Kyra Woodworth',
    profile_photo_url: '',
    rating: 5,
    relative_time_description: 'a year ago',
    text: 'I had a such a comprehensive and attentive first visit with Chelsea after a long time of not going to the dentist. She helped me feel so comfortable, explained everything that was happening very clearly and made sure to address each of my concerns. The doctor was similarly helpful and the front staff were so pleasant. I will definitely be returning for my regular care!',
    author_url: GOOGLE_REVIEW_URL,
    time: Math.floor(Date.now()/1000) - 500000,
  },
  {
    author_name: 'Emily M',
    profile_photo_url: '',
    rating: 5,
    relative_time_description: 'a year ago',
    text: 'The whole team was so kind and made the entire appointment painless and easy. Chelsea especially was so thorough and took the time to explain everything and ask me how I was feeling along the way. Can actually say I like the dentist for the first time ever! Highly recommend!!',
    author_url: GOOGLE_REVIEW_URL,
    time: Math.floor(Date.now()/1000) - 10368000,
  },
  {
    author_name: 'Nicole Titus',
    profile_photo_url: '',
    rating: 5,
    relative_time_description: 'a year ago',
    text: 'I’ve always had a wonderful experience at this practice. It’s always very clean. The front desk staff is always friendly and helpful. Angel is usually my hygienist and is kind, gentle, and fantastic at making you feel your best after your cleaning. Dr. Sopinsky is nice and takes the time to answer any questions you may have.',
    author_url: GOOGLE_REVIEW_URL,
    time: Math.floor(Date.now()/1000) - 10500000,
  },
];
